import {
  Alert,
  BusinessIcon,
  Card,
  OpenInNewIcon,
  Progress,
  Skeleton,
  TagIcon,
  Text,
} from '@billda/design-system';
import { useNavigate } from 'react-router-dom';
import { PermissionGate } from '../../../../shared/components/gates/PermissionGate';
import { useSiteDetails } from '../../../sites/hooks/useSiteDetails';
import { formatCurrency } from '../../../../shared/utils/currency';
import { What3WordsLink } from '../../../../shared/components/atoms/What3WordsLink';
import { SiteEventsTimeline } from '../../../sites/components/timeline/SiteEventsTimeline';

interface SiteDrawerContentProps {
  siteId: number;
}

export function SiteDrawerContent({ siteId }: SiteDrawerContentProps) {
  const navigate = useNavigate();
  const { data: site, error, isFetching: isLoading } = useSiteDetails(siteId);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex flex-col gap-4">
          <Skeleton variant="rectangular" height={150} />
          <Skeleton variant="rectangular" height={100} />
        </div>
      </div>
    );
  }

  if (error || !site) {
    return (
      <div className="p-6">
        <Alert severity="error">
          {error instanceof Error
            ? error.message
            : 'Failed to load site details'}
        </Alert>
      </div>
    );
  }

  const plotCount = site.plots?.length ?? 0;
  const labourItemCount = 0;

  return (
    <div className="p-6">
      <div className="flex flex-col gap-6">
        {/* Site Overview Card */}
        <Card className="p-4">
          <div className="mb-4 flex items-center gap-4">
            <BusinessIcon
              aria-hidden="true"
              className="size-10 text-content-accent"
            />
            <div className="min-w-0 flex-1">
              <Text variant="h6">{site.name}</Text>
              {site.slug && (
                <div
                  className="flex items-center gap-1"
                  data-testid="site-slug"
                >
                  <TagIcon
                    aria-hidden="true"
                    className="size-4 text-content-secondary"
                  />
                  <Text variant="body2" color="secondary">
                    {site.slug}
                  </Text>
                </div>
              )}
              {site.what3WordsAddress && (
                <What3WordsLink address={site.what3WordsAddress} />
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              navigate(`/sites/${siteId}`);
            }}
            className="mb-4 flex cursor-pointer items-center gap-1 text-content-accent underline"
          >
            View full site details
            <OpenInNewIcon aria-hidden="true" className="size-4" />
          </button>

          <div className="flex flex-col gap-2">
            <div className="flex justify-between">
              <Text variant="body2" color="secondary">
                Plots
              </Text>
              <Text variant="body2" className="font-medium">
                {plotCount}
              </Text>
            </div>
            <div className="flex justify-between">
              <Text variant="body2" color="secondary">
                Labour Items
              </Text>
              <Text variant="body2" className="font-medium">
                {labourItemCount}
              </Text>
            </div>
          </div>
        </Card>

        {/* Financial Metrics - Permission Gated */}
        <PermissionGate
          permission="sites.financial.read"
          fallback={null}
          showRequiredPermissions={false}
        >
          {site.financialMetrics && (
            <Card className="p-4">
              <Text
                variant="body2"
                color="secondary"
                className="mb-2 font-semibold"
              >
                Financial Summary
              </Text>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="mb-1 flex justify-between">
                  <Text variant="caption" color="secondary">
                    Progress
                  </Text>
                  <Text variant="caption" className="font-medium">
                    {site.financialMetrics.progress.toFixed(1)}%
                  </Text>
                </div>
                <Progress
                  variant="determinate"
                  value={Math.min(site.financialMetrics.progress, 100)}
                  className="h-2 rounded-lg"
                />
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex justify-between">
                  <Text variant="body2" color="secondary">
                    Total Value
                  </Text>
                  <Text variant="body2" className="font-medium">
                    {formatCurrency(site.financialMetrics.totalLabourPrice)}
                  </Text>
                </div>
                <div className="flex justify-between">
                  <Text variant="body2" color="secondary">
                    Approved
                  </Text>
                  <Text variant="body2" className="font-medium">
                    {formatCurrency(site.financialMetrics.approvedAmount)}
                  </Text>
                </div>
                <div className="flex justify-between">
                  <Text variant="body2" color="secondary">
                    Booked
                  </Text>
                  <Text variant="body2" className="font-medium">
                    {formatCurrency(site.financialMetrics.bookedAmount)}
                  </Text>
                </div>
                <div className="flex justify-between">
                  <Text variant="body2" color="secondary">
                    Variations
                  </Text>
                  <Text variant="body2" className="font-medium">
                    {formatCurrency(site.financialMetrics.variationsAmount)}
                  </Text>
                </div>
                <div className="flex justify-between">
                  <Text variant="body2" color="secondary">
                    Remaining
                  </Text>
                  <Text variant="body2" className="font-medium">
                    {formatCurrency(site.financialMetrics.remaining)}
                  </Text>
                </div>
              </div>
            </Card>
          )}
        </PermissionGate>

        {/* Site History Timeline */}
        <Card className="p-4">
          <Text
            variant="body2"
            color="secondary"
            className="mb-2 font-semibold"
          >
            Site History
          </Text>
          <SiteEventsTimeline siteId={siteId} />
        </Card>
      </div>
    </div>
  );
}
